import { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiPatient from 'hooks/useRoiPatient';
import { sortBy } from 'lodash';
import PeriodComparison from 'pages/OnlineSchedulingReportPage/PeriodComparison/PeriodComparison';
import {
  LABEL_FORMAT_BY_GRANULARITY,
  bucketTimeSeries,
  getGranularityForRange,
} from 'utils/bucketTimeSeries';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const EXISTING_COLOR = '#B01BAE';
const NEW_COLOR = '#60A5FA';

interface TotalPatientChartProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const TotalPatientChart: FC<TotalPatientChartProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data: roiPatient, isLoading, isValidating } = useRoiPatient({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const { chartData, bucketed, granularity, avgPerDay } = useMemo(() => {
    const dailyDetails = roiPatient?.periodData?.details
      ? sortBy(roiPatient.periodData.details, 'date')
      : [];
    const resolvedGranularity = getGranularityForRange(
      selectedDateRange.fromDate,
      selectedDateRange.toDate
    );
    const bucketedEntries = bucketTimeSeries(dailyDetails, resolvedGranularity, [
      'totalExistingPatients',
      'totalNewPatients',
      'totalPatients',
    ]);
    const labelFormat = LABEL_FORMAT_BY_GRANULARITY[resolvedGranularity];

    return {
      bucketed: bucketedEntries,
      granularity: resolvedGranularity,
      avgPerDay: dailyDetails.length
        ? Math.round(
            dailyDetails.reduce((sum, d) => sum + d.totalPatients, 0) /
              dailyDetails.length
          )
        : 0,
      chartData: {
        labels: bucketedEntries.map((d) => moment(d.date).format(labelFormat)),
        datasets: [
          {
            label: 'Existing patients',
            data: bucketedEntries.map((d) => d.totalExistingPatients),
            borderColor: EXISTING_COLOR,
            backgroundColor: EXISTING_COLOR,
            pointBackgroundColor: EXISTING_COLOR,
            pointRadius: 5,
            pointHoverRadius: 6,
            fill: false,
            tension: 0,
            borderWidth: 2,
          },
          {
            label: 'New patients',
            data: bucketedEntries.map((d) => d.totalNewPatients),
            borderColor: NEW_COLOR,
            backgroundColor: NEW_COLOR,
            pointBackgroundColor: NEW_COLOR,
            pointRadius: 5,
            pointHoverRadius: 6,
            fill: false,
            tension: 0,
            borderWidth: 2,
          },
        ],
      },
    };
  }, [roiPatient, selectedDateRange.fromDate, selectedDateRange.toDate]);

  const totalPatients = roiPatient?.periodData?.totalPatients ?? 0;
  const lastTotalPatients = roiPatient?.lastPeriodData?.totalPatients ?? 0;

  if (isLoading || isValidating) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <span className="text-30 font-bold text-magenta">
          {totalPatients.toLocaleString()}
        </span>
        <div className="flex items-center gap-x-1.6 text-14 text-darkest-grey">
          <span>
            <span className="font-bold">{avgPerDay.toLocaleString()}</span> avg
            / day
          </span>
          <PeriodComparison current={totalPatients} last={lastTotalPatients} />
        </div>
      </div>

      <div className="pr-2.3 flex-1">
        <Line
          data={chartData}
          className="max-h-[30rem]"
          options={{
            maintainAspectRatio: false,
            plugins: {
              title: { display: false },
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const index = items[0]?.dataIndex ?? 0;
                    const entry = bucketed[index];
                    if (granularity === 'day' && entry) {
                      return moment(entry.date).format('YYYY/MM/DD');
                    }
                    return items[0]?.label ?? '';
                  },
                  label: (ctx) =>
                    `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString()}`,
                },
              },
            },
            scales: {
              y: {
                min: 0,
                grid: {
                  color: '#e5e7eb',
                },
                ticks: {
                  color: '#373737',
                  font: { size: 12 },
                  callback: (value) =>
                    Number.isInteger(Number(value))
                      ? Number(value).toLocaleString()
                      : null,
                },
              },
              x: {
                grid: { display: false },
                ticks: {
                  color: '#373737',
                  font: { size: 12 },
                },
              },
            },
          }}
        />
      </div>

      <div className="flex items-center justify-center gap-x-1.6 text-12 leading-[1.8rem] text-darkest-grey">
        <div className="flex items-center gap-x-0.6">
          <span
            className="w-2 h-2 inline-block flex-shrink-0"
            style={{ backgroundColor: EXISTING_COLOR }}
          />
          <span>Existing patients</span>
        </div>
        <div className="flex items-center gap-x-0.6">
          <span
            className="w-2 h-2 inline-block flex-shrink-0"
            style={{ backgroundColor: NEW_COLOR }}
          />
          <span>New patients</span>
        </div>
      </div>
    </>
  );
};

export default TotalPatientChart;
